/**
 * Dialog for creating a new teammate (agent instance).
 * Collects name, role, instructions, goals, and app integrations.
 * Mirrors field layout from TeammateConfigModal for consistency.
 */

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck } from "@/components/animate-ui/icons/badge-check";
import { Clipboard } from "@/components/animate-ui/icons/clipboard";
import { Layers } from "@/components/animate-ui/icons/layers";
import { PlugZap } from "@/components/animate-ui/icons/plug-zap";
import { User } from "@/components/animate-ui/icons/user";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { authClient } from "@/lib/api/auth-client";
import { useCreateAgent } from "@/lib/api/hooks/tasks";
import { getAppIntegrations, getUserIntegrations } from "@/lib/api/integrations";
import { integrationKeys } from "@/lib/api/query-keys/integrations";

interface CreateTeammateDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const humanizeSegment = (value: string) =>
	value
		.split(/[\s_-]+/)
		.filter(Boolean)
		.map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
		.join(" ");

export function CreateTeammateDialog({ open, onOpenChange }: CreateTeammateDialogProps) {
	const [name, setName] = useState("");
	const [role, setRole] = useState("");
	const [instructions, setInstructions] = useState("");
	const [goals, setGoals] = useState("");
	const [selectedIntegrations, setSelectedIntegrations] = useState<number[]>([]);
	const [enableEmail, setEnableEmail] = useState(false);
	const [visibleIntegrationCount, setVisibleIntegrationCount] = useState(3);
	const createAgent = useCreateAgent();
	const navigate = useNavigate();
	const nameId = useId();
	const roleId = useId();
	const instructionsId = useId();
	const goalsId = useId();

	// Fetch user integrations
	const { data: userIntegrations = [], isLoading: isLoadingIntegrations } = useQuery({
		queryKey: integrationKeys.user(),
		queryFn: getUserIntegrations,
		enabled: open,
		staleTime: 60 * 1000,
	});

	// Fetch app integrations catalog for display names
	const { data: appIntegrations = [] } = useQuery({
		queryKey: integrationKeys.catalog(),
		queryFn: getAppIntegrations,
		staleTime: 5 * 60 * 1000,
	});

	const integrationNameMap = useMemo(() => {
		const map = new Map<number, string>();
		for (const integration of appIntegrations ?? []) {
			map.set(integration.id, integration.name);
		}
		return map;
	}, [appIntegrations]);

	const toggleIntegration = (integrationId: number) => {
		setSelectedIntegrations((prev) =>
			prev.includes(integrationId)
				? prev.filter((id) => id !== integrationId)
				: [...prev, integrationId]
		);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		const result = await createAgent.mutateAsync({
			name: name.trim(),
			role: role.trim() || undefined,
			instructions: instructions.trim() || undefined,
			goals: goals.trim() || undefined,
			integration_ids: selectedIntegrations.length > 0 ? selectedIntegrations : undefined,
		});

		// Enable email inbox after creation (not in AgentInstanceCreate schema)
		if (enableEmail) {
			try {
				await authClient.PATCH("/api/v1/agents/instances/{instance_id}", {
					params: { path: { instance_id: result.id } },
					body: { inbound_email_enabled: true },
				});
			} catch (err) {
				console.error("Failed to enable email inbox:", err);
				toast.error("Teammate created but email inbox setup failed");
			}
		}

		toast.success("Teammate created");
		onOpenChange(false);
		setName("");
		setRole("");
		setInstructions("");
		setGoals("");
		setSelectedIntegrations([]);
		setEnableEmail(false);
		setVisibleIntegrationCount(3);
		navigate({ to: "/teammates/$id", params: { id: String(result.id) } });
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" aria-describedby={undefined}>
				<DialogHeader className="shrink-0">
					<DialogTitle>Create New Mate</DialogTitle>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
					<div className="space-y-6 overflow-y-auto flex-1 px-1">
						{/* Name */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<User className="size-4 text-muted-foreground" animateOnHover />
								<Label htmlFor={nameId} className="text-sm font-medium">
									Name <span className="text-red-600">*</span>
								</Label>
							</div>
							<Input
								id={nameId}
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Research Assistant"
								maxLength={255}
								required
							/>
						</div>

						{/* Role */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Layers className="size-4 text-muted-foreground" animateOnHover />
								<Label htmlFor={roleId} className="text-sm font-medium">
									Role
								</Label>
							</div>
							<Input
								id={roleId}
								value={role}
								onChange={(e) => setRole(e.target.value)}
								placeholder="e.g. Finds and summarizes articles"
								maxLength={200}
							/>
						</div>

						{/* Instructions */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Clipboard className="size-4 text-muted-foreground" animateOnHover />
								<Label htmlFor={instructionsId} className="text-sm font-medium">
									Instructions
								</Label>
							</div>
							<Textarea
								id={instructionsId}
								value={instructions}
								onChange={(e) => setInstructions(e.target.value)}
								rows={4}
								placeholder="Example: Monitor campaign performance, optimize bids based on ROI targets, generate weekly reports..."
								className="resize-y text-base md:text-sm"
							/>
						</div>

						{/* Goals */}
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<BadgeCheck className="size-4 text-muted-foreground" animateOnHover />
								<Label htmlFor={goalsId} className="text-sm font-medium">
									Goals
								</Label>
							</div>
							<Textarea
								id={goalsId}
								value={goals}
								onChange={(e) => setGoals(e.target.value)}
								rows={3}
								placeholder="Example: Reduce cost per acquisition, improve click-through rates, increase conversion rates..."
								className="resize-y text-base md:text-sm"
							/>
						</div>

						{/* Apps */}
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<PlugZap className="size-4 text-muted-foreground" animateOnHover />
								<Label className="text-sm font-medium">Apps</Label>
							</div>

							{isLoadingIntegrations ? (
								<div className="text-sm text-muted-foreground">Loading integrations...</div>
							) : userIntegrations.length === 0 ? (
								<div className="text-sm text-muted-foreground">
									No connected apps available.{" "}
									<Button variant="link" className="h-auto p-0 text-sm" asChild>
										<Link
											to="/integrations"
											search={{
												oauth: undefined,
												provider: undefined,
												message: undefined,
												composio_connection_id: undefined,
												composio_app_integration_id: undefined,
											}}
										>
											See all apps
										</Link>
									</Button>
								</div>
							) : (
								<div className="space-y-2">
									{[...userIntegrations]
										.sort((a, b) => {
											const aSelected = selectedIntegrations.includes(Number(a.app_integration_id));
											const bSelected = selectedIntegrations.includes(Number(b.app_integration_id));
											if (aSelected && !bSelected) return -1;
											if (!aSelected && bSelected) return 1;
											return 0;
										})
										.slice(0, visibleIntegrationCount)
										.map((integration) => {
											const appIntegrationId = Number(integration.app_integration_id);
											return (
												<div key={integration.id} className="flex items-center space-x-2">
													<Checkbox
														id={`create-integration-${integration.id}`}
														checked={selectedIntegrations.includes(appIntegrationId)}
														onCheckedChange={() => toggleIntegration(appIntegrationId)}
													/>
													<label
														htmlFor={`create-integration-${integration.id}`}
														className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
													>
														{integrationNameMap.get(appIntegrationId) ??
															[
																humanizeSegment(integration.provider),
																humanizeSegment(integration.kind),
															]
																.filter(Boolean)
																.join(" ")}
													</label>
												</div>
											);
										})}
									<div className="flex items-center space-x-2 text-xs text-muted-foreground">
										<span aria-hidden className="inline-block size-4" />
										{userIntegrations.length > visibleIntegrationCount && (
											<button
												type="button"
												onClick={() => setVisibleIntegrationCount((prev) => prev + 5)}
												className="hover:text-foreground hover:underline"
											>
												+{userIntegrations.length - visibleIntegrationCount} more
											</button>
										)}
										{userIntegrations.length > visibleIntegrationCount && (
											<span aria-hidden>&middot;</span>
										)}
										<Link
											to="/integrations"
											search={{
												oauth: undefined,
												provider: undefined,
												message: undefined,
												composio_connection_id: undefined,
												composio_app_integration_id: undefined,
											}}
											className="hover:text-foreground hover:underline"
										>
											See all apps.
										</Link>
									</div>
								</div>
							)}
						</div>

						{/* Email Inbox */}
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-2">
									<Mail className="size-4 text-muted-foreground" />
									<Label className="text-sm font-medium">Email Inbox</Label>
								</div>
								<Switch checked={enableEmail} onCheckedChange={setEnableEmail} />
							</div>
							{enableEmail && (
								<p className="text-xs text-muted-foreground">
									A unique email inbox will be created after the teammate is set up.
								</p>
							)}
						</div>
					</div>

					<DialogFooter className="shrink-0 pt-4 border-t mt-4">
						<Button type="submit" disabled={createAgent.isPending || !name.trim()}>
							{createAgent.isPending ? "Creating..." : "Create"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
